import { z } from 'zod';
import {
  addWebhookHeaderIssues,
  webhookHeaderRowSchema,
  webhookHeaderRowsFromNames,
  webhookHeaderRowsToPayload,
} from './WebhookHeaders';

export type IntegrationWebhookField = 'url';

export interface IntegrationWebhookStatusRef {
  id: string;
  name: string;
  commercialName: string | null;
}

export interface IntegrationWebhookLastDelivery {
  shippingNumber: number;
  status: string;
  httpStatus: number | null;
  errorMessage: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export interface IntegrationWebhook {
  id: string;
  name: string;
  status: IntegrationWebhookStatusRef | null;
  urls: string[];
  headerNames: string[];
  active: boolean;
  hasBodyTemplate: boolean;
  requiresTokenRefresh: boolean;
  lockedFields: IntegrationWebhookField[];
  lastDelivery: IntegrationWebhookLastDelivery | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CommerceIntegrationWebhooks {
  commerce: { id: string; name: string };
  webhooks: IntegrationWebhook[];
  notifiableStatuses: IntegrationWebhookStatusRef[];
}

export interface IntegrationWebhookCreatePayload {
  commerceId: string;
  url: string;
  headers: Record<string, string> | null;
  statusIds: string[];
  active: boolean;
}

export interface IntegrationWebhookUpdatePayload {
  url?: string;
  headers?: Record<string, string> | null;
  active?: boolean;
}

export interface IntegrationWebhookDeleteResult {
  id: string;
  result: 'deleted' | 'deactivated';
}

export const WEBHOOK_DELIVERY_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  SENT: 'Enviado',
  DEAD_LETTER: 'Falló, sin más reintentos',
};

export const webhookStatusLabel = (status: IntegrationWebhookStatusRef | null): string =>
  status ? status.commercialName || status.name : 'Todos los estados';

export const integrationWebhookFormSchema = z
  .object({
    isEdit: z.boolean(),
    url: z.string().trim(),
    headers: z.array(webhookHeaderRowSchema),
    statusIds: z.array(z.string()),
    active: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!/^https:\/\/\S+$/i.test(data.url)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: 'Ingrese una URL https válida' });
    }
    if (!data.isEdit && data.statusIds.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['statusIds'], message: 'Seleccione al menos un estado a notificar' });
    }
    addWebhookHeaderIssues(data.headers, 'headers', ctx);
  });

export type IntegrationWebhookFormSchema = z.infer<typeof integrationWebhookFormSchema>;

export const integrationWebhookToFormValues = (
  webhook: IntegrationWebhook | null
): IntegrationWebhookFormSchema => ({
  isEdit: !!webhook,
  url: webhook?.urls[0] ?? '',
  headers: webhookHeaderRowsFromNames(webhook?.headerNames ?? []),
  statusIds: webhook?.status ? [webhook.status.id] : [],
  active: webhook?.active ?? true,
});

export const integrationWebhookFormToCreatePayload = (
  commerceId: string,
  values: IntegrationWebhookFormSchema
): IntegrationWebhookCreatePayload => ({
  commerceId,
  url: values.url,
  headers: webhookHeaderRowsToPayload(values.headers),
  statusIds: values.statusIds,
  active: values.active,
});

export const integrationWebhookFormToUpdatePayload = (
  webhook: IntegrationWebhook,
  values: IntegrationWebhookFormSchema
): IntegrationWebhookUpdatePayload => ({
  active: values.active,
  headers: webhookHeaderRowsToPayload(values.headers),
  ...(!webhook.lockedFields.includes('url') && { url: values.url }),
});
